import type { RouteLocationNormalized } from 'vue-router';

/**
 * Where a navigation leaves the page scrolled.
 *
 * Its own module because it is a decision with three cases and no way to see two of them without
 * driving the browser — the kind of thing that is quietly wrong for months. `composition.ts` and
 * `safe-redirect.ts` next door are pure and tested for the same reason.
 */

/** What `scrollBehavior` may answer: a position, or `false` for «leave it alone». */
export type ScrollDecision = { top: number } | false;

/** The parts of a route this decision reads. Narrow on purpose, so a test can build one. */
export interface ScrollRoute {
  name?: RouteLocationNormalized['name'];
  path: string;
  meta: { slidesIn?: boolean };
}

export function scrollFor(
  to: ScrollRoute,
  from: ScrollRoute,
  savedPosition: { top: number } | null,
): ScrollDecision {
  // A detail sliding in is laid over the page it was opened from, and that one has to stay put
  // until it is covered: the shell scrolls to the top when the slide ends. It slides in when
  // opened from anywhere but another detail, or from one by a link (`savedPosition` is only set
  // for a step through the history); stepping back from a detail to a detail reveals the one
  // underneath instead, and that one is scrolled back to where it was, here.
  const slidesIn =
    to.meta.slidesIn === true &&
    to.name !== from.name &&
    (from.meta.slidesIn !== true || savedPosition === null);

  if (slidesIn) return false;

  /*
   * Same page, different query: the page storing its own state in the URL, not a navigation.
   *
   * This screen keeps a lot in the query — `?tab=`, `?gesture=`, `?peek=` — because a URL that can
   * be pasted is worth having. Treating each of those as a new page threw you to the top: clicking
   * a gesture in the configuration table, halfway down a list of thirty-eight, put the row you had
   * just chosen off the screen, every single time.
   *
   * A step through the history is still honoured. It never goes to the top unasked, so it cannot
   * bring the problem back.
   */
  if (to.path === from.path) return savedPosition ?? false;

  // Anything else: a step through the history goes back to where that page was, a new page starts
  // at the top.
  return savedPosition ?? { top: 0 };
}
